import { type RuleBoxPreview, ruleBoxConfigSchema } from "@loot-os/api-contract";
import type { ParsedRuleBoxForm } from "@loot-os/api-contract/shopifyRuleBoxForm";
import { keepPreviousData, skipToken, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useDebouncedValue } from "./hooks";
import type { PreviewState } from "./PreviewPanel";
import { useTRPC } from "./trpc";

/** How long the form has to stay still before the estimate is asked for again. */
const PREVIEW_DEBOUNCE_MS = 400;

/**
 * The live estimate for the form: `boxes.preview` on the config once the merchant stops typing,
 * the last answer kept on screen while the next loads. `current` is the answer for the form
 * exactly as it stands, and undefined while one is on its way or the form cannot be read. On an
 * edit, `boxId` lets the solver keep an estimated box's current price.
 */
export function useRuleBoxPreview(
  parsed: ParsedRuleBoxForm,
  boxId: string | undefined,
): PreviewState & { current: RuleBoxPreview | undefined } {
  const trpc = useTRPC();
  const key = parsed.ok ? JSON.stringify(parsed.config) : null;
  const settledKey = useDebouncedValue(key, PREVIEW_DEBOUNCE_MS);
  const config = useMemo(
    () => (settledKey === null ? null : ruleBoxConfigSchema.parse(JSON.parse(settledKey))),
    [settledKey],
  );
  const query = useQuery(
    trpc.boxes.preview.queryOptions(config === null ? skipToken : { config, boxId }, {
      placeholderData: keepPreviousData,
    }),
  );
  const updating = key !== settledKey || query.isFetching || query.isPlaceholderData;
  return {
    data: query.data,
    updating,
    error: query.error?.message ?? null,
    formInvalid: key === null,
    current: key !== null && !updating ? query.data : undefined,
  };
}
